/**
 * The settings migration chain — versioned, ordered, idempotent steps over `preset.settings`.
 *
 * A preset is stored data with a shape that outlives the code that wrote it. When the shape changes, every stored preset is suddenly wrong, and the failure is SILENT: a reader looking for a key that moved finds nothing, falls back to `initial`, and the user's saved settings quietly reset. This chain is what stands between a schema change and that outcome.
 *
 * DESIGN RULES, each one load-bearing:
 *
 * 1. EVERY STEP MUST BE IDEMPOTENT. `_v` is a fast path, never the thing preventing corruption.
 *    A step that is only safe because a version stamp gated it is one stray re-run from destroying data — and stray re-runs happen (a redeploy, a rollback, a row written during the window). A key relocation is naturally idempotent: once `inpaint` exists there is nothing left to lift. Prefer transforms with that property; a non-invertible value curve is exactly what this chain exists to avoid ever writing again.
 *
 * 2. PURE AND STORE-FREE. No RTK Query, no DynamoDB, no store import. Each step is a plain
 *    function over a settings object, so it is unit-testable against real stored fixtures.
 *
 * 3. ONE IMPLEMENTATION, TWO CALLERS. The read-time guard (every cache boundary and every preset
 *    load) and any future batch pass run the SAME chain. A batch pass that reimplemented the transform would be a second source of truth for what the data means.
 *
 * 4. NO WRITE-BACK from the read path. The guard is idempotent and runs on every load, so an
 *    unmigrated preset stays correct forever — just re-converted each time. Writing back turns every preset VIEW into a mutation, including on someone else's data during a cross-user load, and makes a guard bug catastrophic instead of recoverable.
 */
import { controlGroup } from '~/lib/migrations/steps/controlGroup'
import { noiseToMotion } from '~/lib/migrations/steps/noiseToMotion'
import { originToPivot } from '~/lib/migrations/steps/originToPivot'
import { rectToPath } from '~/lib/migrations/steps/rectToPath'
import { STAGE_CENTRE_VERSION, stageCentre } from '~/lib/migrations/steps/stageCentre'
import { uniformsToSourceSettings } from '~/lib/migrations/steps/uniformsToSourceSettings'
import { videoToFill } from '~/lib/migrations/steps/videoToFill'

/** Current settings shape version, stamped by `migrateSettings` on every load. v9 moves `x`/`y` to the pivot (`originToPivot`); v10 moves the root frame's origin to the stage centre (`stageCentre`). */
export const SETTINGS_VERSION = STAGE_CENTRE_VERSION

/** Empty while the corpus is current: a shape change adds its step here, migrates the corpus with `scripts/dev/migrate-*.test.js`, then removes it. Each step runs on key evidence and is idempotent, so `from`/`to` are documentation — except `origin-to-pivot` and `stage-centre`, which have no key evidence and gate on the INCOMING `_v`; `stage-centre` runs after every step that writes positions, over positions those steps wrote in the old frame. */
export const MIGRATIONS = [
	{
		from: 8,
		to: 8,
		name: 'uniforms-to-source-settings',
		migrate: uniformsToSourceSettings,
	},
	{
		from: 8,
		to: 9,
		name: 'origin-to-pivot',
		migrate: originToPivot,
	},
	{
		from: 9,
		to: 9,
		name: 'video-to-fill',
		migrate: videoToFill,
	},
	{
		from: 9,
		to: 9,
		name: 'rect-to-path',
		migrate: rectToPath,
	},
	{
		from: 9,
		to: 10,
		name: 'stage-centre',
		migrate: stageCentre,
	},
	{
		from: 10,
		to: 10,
		name: 'noise-to-motion',
		migrate: noiseToMotion,
	},
	{
		from: 10,
		to: 10,
		name: 'control-group',
		migrate: controlGroup,
	},
]

const futureListeners = new Set()

/** `fn(v)` runs whenever settings stamped newer than `SETTINGS_VERSION` arrive; returns the unsubscribe. Keeps the chain store-free while the app reacts (versionCheck reloads). */
export const onFutureSettings = fn => {
	futureListeners.add(fn)
	return () => futureListeners.delete(fn)
}

/** Settings written by a newer client: this code cannot know what their shape means. */
export const isFutureSettings = settings => typeof settings?._v === 'number' && settings._v > SETTINGS_VERSION

/**
 * Run every applicable step over one preset's settings.
 *
 * ⚠️ A FUTURE `_v` IS NEVER DOWNGRADED. Restamped to this version, data a newer client already transformed would be transformed again by the next newer-client load (pivot.md). It passes through stamped as it came, and the listeners hear of it.
 *
 * ⚠️ `_v` ALONE CANNOT GATE THIS. The stamp goes on at every load, so a preset can carry a current stamp AND legacy keys — it was stamped by an earlier version's load path, which knew nothing about the keys a later version would move. Every step therefore runs on KEY EVIDENCE and is idempotent; the version is only used to skip work that is provably done, never to decide correctness.
 *
 * That is why the loop below runs the chain regardless of `_v` and lets each step no-op. The stamp is an optimization; the step is the guarantee.
 */
export function migrateSettings(settings) {
	if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return settings
	if (isFutureSettings(settings)) {
		for (const fn of futureListeners) fn(settings._v)
		return settings
	}

	let out = settings
	for (const step of MIGRATIONS) {
		out = step.migrate(out)
	}
	return out._v === SETTINGS_VERSION ? out : { ...out, _v: SETTINGS_VERSION }
}

export default migrateSettings
